import PropTypes from 'prop-types';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';

import Icon from '@wetransfer/transfer/component/icon';
import LanguagePickerItems, {
  propTypes as itemsPropTypes,
} from '@wetransfer/transfer/component/languagepicker/items';
import { OSLO_GRAY } from '@wetransfer/transfer/constant/colors';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import Analytics from '@wetransfer/transfer/modules/analytics';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import Translations from '@wetransfer/transfer/modules/i18n/dictionary';
import History from '@wetransfer/transfer/modules/router/history';
import Actions from '@wetransfer/transfer/store/action';

import useOutsideClick from './useOutsideClick';

export const LANGUAGES = Object.keys(Translations.locales).map((locale) => ({
  value: locale,
  label: Translations.locales[locale][0], // 0 = Native language, 1 = English
}));

export const LanguagePicker = ({
  languages,
  currentLanguage,
  changeLanguage,
}) => {
  const targetRef = useRef(null);
  const listRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const _d = useState(null),
    focusedButtonIndex = _d[0],
    setFocusedLinkIndex = _d[1];

  const foundLanguage = languages.find(
    (language) => language.value === currentLanguage
  );

  useOutsideClick(listRef, targetRef, () => {
    setIsOpen(false);
  });

  useEffect(() => {
    //override language
    const urlParams = new URLSearchParams(window.location.search);
    const overrideLanguage = urlParams.get('overrideLanguage');

    if (overrideLanguage) {
      Cookie.set(AppConfig.cookies.overrideLanguage.name, overrideLanguage, {
        domain: window.location.origin,
      });

      changeLanguage(overrideLanguage);

      //remove urlParams
      History.replace(window.location.origin + window.location.pathname);
    }
  }, []);

  const handleLanguageChange = async (language) => {
    await changeLanguage(language);

    Analytics.trackSnowplowEvent({
      category: 'language',
      action: 'click_language_drop_down',
      label: language,
    });
  };

  const toggleLanguagePicker = () => {
    setIsOpen(!isOpen);
  };

  const keyboardEventHasModfiers = (event) => {
    return event.altKey || event.shiftKey || event.metaKey || event.ctrlKey;
  };

  const getCurrentLanguageIndex = () => {
    return languages.findIndex((e) => e.value === currentLanguage);
  };

  const focusTargetButton = useCallback(() => {
    let _a;

    const button =
      (_a = targetRef.current) === null || _a === void 0 ? void 0 : _a;

    if (button && button instanceof HTMLButtonElement) {
      button.focus();
    }
  }, []);

  const focusButtonAtIndex = useCallback((index) => {
    let _button;
    if (!listRef.current) return;
    const anchors = Array.from(listRef.current.querySelectorAll('button'));

    if (!((_button = anchors[index]) === null || _button === void 0)) {
      _button.focus();
    }
  }, []);

  const handleButtonFocus = useCallback((event) => {
    if (!listRef.current) return;
    const anchors = Array.from(listRef.current.querySelectorAll('button'));
    const index = anchors.indexOf(event.target);
    setFocusedLinkIndex(index !== -1 ? index : null);
  }, []);

  // Events
  const handleTargetKeyDown = useCallback(
    (event) => {
      if (keyboardEventHasModfiers(event)) return;

      switch (event.key) {
        case 'Enter':
        case ' ':
          if (isOpen) {
            setIsOpen(false);
            focusButtonAtIndex(languages.length - 1);
          } else {
            setIsOpen(true);
            focusButtonAtIndex(getCurrentLanguageIndex());
          }
          event.preventDefault();
          break;
        case 'ArrowUp':
        case 'ArrowDown':
          setIsOpen(true);
          focusButtonAtIndex(getCurrentLanguageIndex());
          event.preventDefault();
          break;
      }
    },
    [isOpen, focusButtonAtIndex]
  );

  const handleListKeyDown = useCallback(
    (event) => {
      const prev = focusedButtonIndex - 1;
      const next = focusedButtonIndex + 1;

      if (keyboardEventHasModfiers(event)) return;

      switch (event.key) {
        case 'ArrowUp':
          focusButtonAtIndex(prev >= 0 ? prev : languages.length - 1);
          event.preventDefault();
          break;
        case 'ArrowDown':
          focusButtonAtIndex(next <= languages.length - 1 ? next : 0);
          event.preventDefault();
          break;
        case 'Escape':
          setIsOpen(false);
          focusTargetButton();
          event.preventDefault();
          break;
      }
    },
    [languages, isOpen, focusButtonAtIndex, focusedButtonIndex]
  );

  if (!foundLanguage) {
    return null;
  }

  const items = (
    <LanguagePickerItems
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      languages={languages}
      listRef={listRef}
      currentLanguage={foundLanguage}
      changeLanguage={handleLanguageChange}
      handleButtonFocus={handleButtonFocus}
      handleListKeyDown={handleListKeyDown}
    />
  );

  return (
    <>
      <button
        ref={targetRef}
        aria-controls="languagepicker_menu-id"
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-haspopup="true"
        aria-label="WeTransfer languages"
        className="languagepicker__current"
        id="languagepicker_target-button"
        tabIndex="0"
        type="button"
        onClick={toggleLanguagePicker}
        onKeyDown={handleTargetKeyDown}
      >
        {foundLanguage.label}
        <Icon
          color={OSLO_GRAY}
          className="languagepicker__arrow"
          type="Arrow"
          orientation="bottom"
        />
      </button>
      {items}
    </>
  );
};
LanguagePicker.propTypes = {
  languages: itemsPropTypes.languages,
  currentLanguage: PropTypes.string,
  changeLanguage: itemsPropTypes.changeLanguage,
};
LanguagePicker.defaultProps = {
  languages: LANGUAGES,
};

const mapStateToProps = ({ session: { language } }) => ({
  currentLanguage: language,
});

export const setDOMLanguage = (language) => {
  const docRoot = document.documentElement;

  if (docRoot && docRoot.hasAttribute('lang')) {
    docRoot.setAttribute('lang', language);
  }
};

const mapDispatchToProps = (dispatch) => ({
  changeLanguage: async (language) => {
    await dispatch(Actions.User.updateUserLanguage(language));

    // Update the session store and close poptip
    await dispatch(Actions.Session.setLanguage(language));

    // Update html[lang] if possible
    setDOMLanguage(language);
  },
});

const ConnectedLanguagePicker = connect(
  mapStateToProps,
  mapDispatchToProps
)(LanguagePicker);
ConnectedLanguagePicker.propTypes = {
  popupOptions: PropTypes.object,
};

export default ConnectedLanguagePicker;
